import React, { useState, useEffect, useRef } from 'react';
import apiClient, { API } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, X, Play, Video, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const css = `
@property --feat-angle { syntax: '<angle>'; initial-value: 0deg; inherits: false; }
@keyframes feat-border-spin { to { --feat-angle: 360deg; } }
.feat-card {
  position: relative; border-radius: 20px; padding: 3px;
  background: conic-gradient(from var(--feat-angle, 0deg), #f0f, #0ff, #ff0, #f0f);
  animation: feat-border-spin 4s linear infinite;
  box-shadow: 0 0 28px rgba(217,70,239,0.4), 0 0 70px rgba(34,211,238,0.18);
  transition: transform 0.35s cubic-bezier(0.22,1,0.36,1), box-shadow 0.35s ease;
}
.feat-card:hover {
  transform: translateY(-6px) scale(1.015);
  box-shadow: 0 0 44px rgba(217,70,239,0.65), 0 0 110px rgba(34,211,238,0.3), 0 18px 40px rgba(0,0,0,0.5);
}
.feat-inner {
  border-radius: 17px; overflow: hidden; position: relative;
  background: radial-gradient(ellipse at 30% 20%, #1e1b4b 0%, #0b0618 55%, #000 100%);
}
.feat-stars, .feat-stars2 {
  position: absolute; inset: -50%; pointer-events: none;
  background-image: radial-gradient(2px 2px at 20% 30%, #fff, transparent),
    radial-gradient(1.5px 1.5px at 60% 70%, #a5f3fc, transparent),
    radial-gradient(2px 2px at 80% 20%, #f0abfc, transparent),
    radial-gradient(1px 1px at 40% 80%, #fff, transparent),
    radial-gradient(1.5px 1.5px at 90% 60%, #fde68a, transparent),
    radial-gradient(1px 1px at 10% 60%, #fff, transparent);
  background-size: 220px 220px; opacity: 0.8;
  animation: feat-drift 40s linear infinite;
}
.feat-stars2 { background-size: 340px 340px; opacity: 0.5; animation-duration: 70s; animation-direction: reverse; }
@keyframes feat-drift { to { transform: translate(120px, 80px) rotate(8deg); } }
.feat-nebula {
  position: absolute; inset: 0; pointer-events: none; opacity: 0.55;
  background:
    radial-gradient(circle at 78% 12%, rgba(217,70,239,0.35), transparent 42%),
    radial-gradient(circle at 12% 82%, rgba(34,211,238,0.3), transparent 46%),
    radial-gradient(circle at 55% 55%, rgba(129,140,248,0.16), transparent 55%);
  animation: feat-nebula-pulse 7s ease-in-out infinite alternate;
}
@keyframes feat-nebula-pulse { from { opacity: 0.35; } to { opacity: 0.75; } }
.feat-shoot {
  position: absolute; top: 12%; left: -30%; width: 90px; height: 2px; pointer-events: none;
  background: linear-gradient(90deg, transparent, #fff, transparent);
  transform: rotate(-18deg);
  animation: feat-shoot 6s linear infinite;
}
@keyframes feat-shoot {
  0%, 74% { left: -30%; top: 8%; opacity: 0; }
  76% { opacity: 1; }
  88% { left: 115%; top: 46%; opacity: 0; }
  100% { left: 115%; top: 46%; opacity: 0; }
}
.feat-avatar { animation: feat-glow-pulse 3.2s ease-in-out infinite; }
@keyframes feat-glow-pulse {
  0%, 100% { box-shadow: 0 0 16px rgba(217,70,239,0.5); }
  50% { box-shadow: 0 0 30px rgba(34,211,238,0.75); }
}
.feat-badge {
  background: linear-gradient(90deg, #d946ef, #22d3ee, #fde047, #d946ef);
  background-size: 300% 100%;
  animation: feat-badge-slide 4s linear infinite;
}
@keyframes feat-badge-slide { to { background-position: 300% 0; } }
`;

const ytEmbed = (url) => {
  const m = (url || '').match(/(?:youtu\.be\/|v=|shorts\/|embed\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

export const FeaturedArtists = ({ user }) => {
  const [featured, setFeatured] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [uploading, setUploading] = useState(false);
  const videoInputRef = useRef(null);
  const [form, setForm] = useState({ name: '', tagline: '', photo_url: '', soundcloud_url: '', spotify_url: '', instagram_url: '', youtube_url: '', video_url: '', genres: '' });
  const isStaff = ['admin', 'owner', 'overseer'].includes(user?.role);

  const load = async () => {
    try {
      const res = await fetch(`${API}/main-artists`);
      const data = await res.json();
      setFeatured(data.featured || []);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const handleVideoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!['video/mp4', 'video/webm', 'video/quicktime'].includes(file.type)) {
      return toast.error('Use MP4, WebM, or MOV video');
    }
    if (file.size > 50 * 1024 * 1024) return toast.error('Video too large — max 50MB');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'main-artist-videos');
      const res = await apiClient.post('/upload-file', fd);
      setForm((f) => ({ ...f, video_url: res.data.url }));
      toast.success('Video uploaded 🎬');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Video upload failed');
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    try {
      await apiClient.post('/main-artists', {
        ...form,
        genres: form.genres.split(',').map((g) => g.trim()).filter(Boolean),
      });
      toast.success(`${form.name} added to the Main Roster ⭐`);
      setShowAdd(false);
      setForm({ name: '', tagline: '', photo_url: '', soundcloud_url: '', spotify_url: '', instagram_url: '', youtube_url: '', video_url: '', genres: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
  };

  const remove = async (fid) => {
    try { await apiClient.delete(`/main-artists/${fid}`); load(); toast.success('Removed'); }
    catch (e) { toast.error('Failed to remove'); }
  };

  if (!featured.length && !isStaff) return null;

  return (
    <div className="mb-10" data-testid="featured-artists-section">
      <style>{css}</style>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg md:text-lg font-black tracking-wide bg-gradient-to-r from-fuchsia-400 via-cyan-300 to-yellow-300 bg-clip-text text-transparent">
            ✦ MAIN ROSTER — RAZIENT SPONSORED ✦
          </h2>
          <p className="text-white/40 text-xs">Our headline artists — booked and managed end-to-end by the Razient team</p>
        </div>
        {isStaff && (
          <Button size="sm" onClick={() => setShowAdd(true)} className="bg-fuchsia-600 hover:bg-fuchsia-500" data-testid="add-featured-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Main Artist
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {featured.map((f) => {
          const embed = ytEmbed(f.youtube_url);
          const hasVideo = !!f.video_url || !!embed;
          return (
            <div key={f.featured_id} className="feat-card" data-testid={`featured-card-${f.featured_id}`}>
              <div className="feat-inner p-4">
                <div className="feat-stars" />
                <div className="feat-stars2" />
                <div className="feat-nebula" />
                <div className="feat-shoot" />
                <div className="absolute top-0 right-0 z-20">
                  <span className="feat-badge inline-block text-[9px] font-black tracking-widest text-black px-3 py-1 rounded-bl-xl">
                    ★ MAIN ARTIST ★
                  </span>
                </div>
                <div className="relative z-10">
                  <div className="flex items-center gap-4">
                    {f.photo_url ? (
                      <img src={f.photo_url} alt={f.name} className="feat-avatar w-20 h-20 rounded-full object-cover ring-2 ring-fuchsia-400/60" />
                    ) : (
                      <div className="feat-avatar w-20 h-20 rounded-full bg-fuchsia-900/50 flex items-center justify-center text-3xl">🎧</div>
                    )}
                    <div className="min-w-0">
                      <h3 className="text-white font-black text-lg truncate">{f.name}</h3>
                      {f.tagline && <p className="text-cyan-200/70 text-xs italic line-clamp-2">{f.tagline}</p>}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(f.genres || []).slice(0, 4).map((g) => (
                          <span key={g} className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-fuchsia-200">{g}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {hasVideo && (
                    playing === f.featured_id ? (
                      <div className="mt-3 aspect-video rounded-lg overflow-hidden border border-fuchsia-500/40 shadow-[0_0_20px_rgba(217,70,239,0.3)]">
                        {f.video_url ? (
                          <video src={`${BACKEND}${f.video_url}`} controls autoPlay playsInline className="w-full h-full bg-black" data-testid={`featured-video-${f.featured_id}`} />
                        ) : (
                          <iframe src={`${embed}?autoplay=1`} title={f.name} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
                        )}
                      </div>
                    ) : (
                      <button onClick={() => setPlaying(f.featured_id)} data-testid={`featured-play-${f.featured_id}`}
                        className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-cyan-400/30 text-cyan-200 text-sm transition-colors">
                        <Play className="w-4 h-4" /> Watch video
                      </button>
                    )
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {f.soundcloud_url && <a href={f.soundcloud_url} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-full bg-orange-500/20 text-orange-300 hover:bg-orange-500/30 transition-colors">SoundCloud</a>}
                    {f.spotify_url && <a href={f.spotify_url} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-full bg-green-500/20 text-green-300 hover:bg-green-500/30 transition-colors">Spotify</a>}
                    {f.instagram_url && <a href={f.instagram_url} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-full bg-pink-500/20 text-pink-300 hover:bg-pink-500/30 transition-colors">Instagram</a>}
                    {isStaff && (
                      <button onClick={() => remove(f.featured_id)} className="ml-auto text-red-400/70 hover:text-red-300" data-testid={`featured-delete-${f.featured_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setShowAdd(false)}>
          <div className="bg-gray-900 border border-fuchsia-500/40 rounded-xl p-5 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto" data-testid="featured-add-modal">
            <div className="flex justify-between items-center">
              <h3 className="text-white font-bold">⭐ Add Main Artist</h3>
              <button onClick={() => setShowAdd(false)}><X className="w-4 h-4 text-white/60" /></button>
            </div>
            <Input placeholder="Artist name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="featured-name-input" />
            <Input placeholder="Tagline (e.g. 'Riddim heavyweight')" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
            <Input placeholder="Photo URL" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} />
            <input ref={videoInputRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={handleVideoFile} data-testid="featured-video-input" />
            <button
              onClick={() => !uploading && videoInputRef.current?.click()}
              className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border text-sm transition-colors ${form.video_url ? 'border-emerald-400/50 bg-emerald-500/10 text-emerald-300' : 'border-cyan-400/40 bg-white/5 text-cyan-200 hover:bg-white/10'}`}
              data-testid="featured-upload-video-btn"
            >
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading video…</>
                : form.video_url ? <><Video className="w-4 h-4" /> Video attached ✓ (tap to replace)</>
                : <><Video className="w-4 h-4" /> Upload video (MP4/WebM · max 50MB)</>}
            </button>
            <Input placeholder="SoundCloud URL" value={form.soundcloud_url} onChange={(e) => setForm({ ...form, soundcloud_url: e.target.value })} />
            <Input placeholder="Spotify URL" value={form.spotify_url} onChange={(e) => setForm({ ...form, spotify_url: e.target.value })} />
            <Input placeholder="Instagram URL" value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} />
            <Input placeholder="YouTube video URL (optional fallback)" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} />
            <Input placeholder="Genres (comma separated: riddim, dubstep)" value={form.genres} onChange={(e) => setForm({ ...form, genres: e.target.value })} />
            <Button className="w-full bg-fuchsia-600 hover:bg-fuchsia-500" onClick={save} disabled={uploading} data-testid="featured-save-btn">Add to Main Roster</Button>
          </div>
        </div>
      )}
    </div>
  );
};
